import React, { useState } from 'react';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import Header from './components/Header2';
import Footer from './components/Footer2';

const FAQs = () => {
    const [openIndex, setOpenIndex] = useState(null);

    const faqs = [
        {
            category: 'Orders & Shopping',
            questions: [
                { question: 'How do I place an order?', answer: 'Browse our collection, add items to your cart, and proceed to checkout. You can pay using credit/debit cards, UPI, net banking, or cash on delivery. Once your order is confirmed, you will receive a confirmation email with tracking details.' },
                { question: 'Can I modify or cancel my order?', answer: 'Orders can be modified or cancelled within 2 hours of placement. After this window, orders enter our processing system. Please contact our customer service team at hello@rang.in immediately if you need changes.' },
                { question: 'Do you offer custom sizing?', answer: 'Yes! Many of our products can be customized to your measurements. Look for the "Custom Size" option on product pages, or contact us for bespoke orders. Custom orders typically take 2-3 weeks additional time.' },
            ],
        },
        {
            category: 'Shipping & Delivery',
            questions: [
                { question: 'What are the shipping charges?', answer: 'We offer free shipping on all orders above \u20B95,000 within India. Standard shipping is \u20B9199 for orders below this amount. Express shipping is available at \u20B9399 for delivery within 2-3 business days.' },
                { question: 'How long does delivery take?', answer: 'Standard delivery takes 5-7 business days across India. Metro cities usually receive orders within 3-5 business days. Express delivery is available in select cities with 2-3 business day delivery.' },
                { question: 'Do you ship internationally?', answer: 'Yes, we ship to select international destinations including USA, UK, UAE, Singapore, and Australia. International shipping charges and delivery times vary by location. Please check at checkout for exact rates.' },
            ],
        },
        {
            category: 'Returns & Exchanges',
            questions: [
                { question: 'What is your return policy?', answer: 'We offer a 7-day return policy from the date of delivery. Items must be unused, unwashed, with original tags attached. Bridal and custom-made pieces are non-returnable. Refunds are processed within 7-10 business days.' },
                { question: 'How do I initiate a return?', answer: 'Go to your Orders page, select the order, and click "Return/Exchange". Choose the items you wish to return, select a reason, and schedule a pickup. Our delivery partner will collect the package from your doorstep.' },
                { question: 'Can I exchange for a different size?', answer: 'Yes, size exchanges are free of charge. You can request an exchange through your Orders page. The replacement will be shipped within 3-5 business days after we receive your original item.' },
            ],
        },
        {
            category: 'Product & Care',
            questions: [
                { question: 'How should I care for my sarees?', answer: 'We recommend dry cleaning for silk and Banarasi sarees. Cotton sarees can be gently hand-washed with mild detergent. Always store in muslin cloth to maintain the fabric quality. Avoid direct sunlight for extended periods.' },
                { question: 'Are the colours shown accurate?', answer: 'We make every effort to display accurate colours. However, slight variations may occur due to screen settings and lighting. Our product descriptions include detailed colour information to help you make the right choice.' },
            ],
        },
    ];

    let globalIndex = 0;

    return (
        <div className="w-full min-h-screen bg-[#0A0A0A]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />
            <Header />

            <main className="pt-[90px] md:pt-[110px] pb-16">
                {/* Hero */}
                <div className="px-4 md:px-6 pb-8 md:pb-12 text-center border-b border-[#2A2A2A]">
                    <HelpCircle className="w-10 h-10 text-[#FF6B35] mx-auto mb-4" />
                    <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-wider mb-3 text-white font-['Montserrat']">
                        FAQs
                    </h1>
                    <p className="text-sm text-[#A3A3A3] font-['Montserrat']">Everything you need to know about shopping with RANG</p>
                </div>

                <div className="max-w-[800px] mx-auto px-4 md:px-6 pt-10">
                    {faqs.map((section) => (
                        <div key={section.category} className="mb-10">
                            <h2 className="text-base font-bold text-[#FF6B35] mb-4 font-['Montserrat'] uppercase tracking-wider">
                                {section.category}
                            </h2>
                            <div className="space-y-2">
                                {section.questions.map((faq) => {
                                    const idx = globalIndex++;
                                    const isOpen = openIndex === idx;
                                    return (
                                        <div key={idx} className="bg-[#1A1A1A] border border-[#2A2A2A] overflow-hidden">
                                            <button
                                                onClick={() => setOpenIndex(isOpen ? null : idx)}
                                                className="w-full flex items-center justify-between px-5 py-4 text-left"
                                            >
                                                <span className="text-sm font-medium text-white font-['Montserrat'] pr-4">
                                                    {faq.question}
                                                </span>
                                                {isOpen ? (
                                                    <ChevronUp className="w-4 h-4 text-[#FF6B35] flex-shrink-0" />
                                                ) : (
                                                    <ChevronDown className="w-4 h-4 text-[#525252] flex-shrink-0" />
                                                )}
                                            </button>
                                            {isOpen && (
                                                <div className="px-5 pb-4">
                                                    <p className="text-sm text-[#A3A3A3] leading-relaxed font-['Montserrat']">
                                                        {faq.answer}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}

                    {/* Contact CTA */}
                    <div className="text-center mt-12 py-8 bg-[#1A1A1A] border border-[#2A2A2A]">
                        <p className="text-sm text-[#A3A3A3] mb-3 font-['Montserrat']">Still have questions?</p>
                        <button
                            onClick={() => window.location.href = '/template2/contact'}
                            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold uppercase tracking-wider bg-[#FF6B35] text-white hover:bg-[#E85D26] transition-colors font-['Montserrat']"
                        >
                            Contact Us
                        </button>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default FAQs;
